import { clamp } from '@/math/angles';
import { forwardX, forwardZ, rightX, rightZ } from '@/math/orientation';

/**
 * collisions.ts — PURE collision resolution (no three.js, no DOM). Each function
 * mutates plain state objects in place and returns whether a collision happened,
 * so the whole thing is deterministic and unit-testable.
 *
 *  - resolveBallCar: sphere vs oriented box (the dribble/push — the heart of it).
 *  - resolveCarCar:  circle separation + momentum exchange (bumping).
 *  - resolveBallBall: elastic-ish sphere collision.
 *  - resolveWall:    lateral clamp + scrape, shared by cars and balls.
 */

/** A car as an oriented box on the ground plane. (CarPhysicsState is compatible.) */
export interface CarBox {
  x: number;
  z: number;
  heading: number;
  vx: number;
  vz: number;
}

/** A ball as a sphere. (BallPhysicsState is compatible.) */
export interface BallBody {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  radius: number;
}

export interface WallResolution {
  pushX: number;
  pushZ: number;
  vx: number;
  vz: number;
}

// Car collision shape (matches the CarView body box footprint, a touch generous).
// M12: the front face widened slightly (1.0 → 1.15) — a bigger "catch" so the
// ball doesn't slip past the nose corner so easily at speed (owner request).
// M153: these are now the STOCK box — the default every ball-contact function
// below takes when the caller passes no `box`. The sim passes each racer's
// own box (tuning.vehicles[id].box via vehicleBoxFor): the ball meets the
// real nose, tail and width of the ride it is pushed by (owner: "wider
// vehicles should have a wider box"), and narrow rides carry a catch margin.
export const CAR_BOX_HALF_LENGTH = 1.9;
export const CAR_BOX_HALF_WIDTH = 1.15;
/** The dimensions of a car's ball-contact box (half extents, world units). */
export interface CarBoxDims {
  halfLength: number;
  halfWidth: number;
}
export const STOCK_CAR_BOX: CarBoxDims = { halfLength: CAR_BOX_HALF_LENGTH, halfWidth: CAR_BOX_HALF_WIDTH };
/** Radius used for car-vs-car circle separation. */
export const CAR_RADIUS = 1.5;

const HARD_HIT = 14; // approach speed above which a ball-car hit pops the ball up
const MAX_POP = 6;
/**
 * Pops may not STACK into a launch (M143c). The pop is added on every step a
 * hard contact lasts, and a ball that is SQUEEZED (between two cars, or a car
 * and a free-kick wall, or a car and the rail) has a hard contact on every
 * step: measured in full-field races, +6 to +12 of climb per step for ten
 * steps, a ball 60 to 240 units up in the air, landing hundreds of units back
 * down the course (the owner's "launched into the sky" report, M67, was one
 * face of this). A ball already rising this fast gets no more lift from a car.
 * Two pops' worth: a single strike on a rolling ball is unchanged, and so is a
 * quick double touch.
 */
const POP_CEILING = MAX_POP * 2;
// The dribble cradle only engages on genuine nose contacts while driving forward.
const CRADLE_MIN_FRONTNESS = 0.55;
const CRADLE_MIN_SPEED = 4;

interface BallPushParams {
  carPush: number;
  maxSpeed: number;
  dribbleAlign: number;
  dribbleLateralGrip: number;
  dribbleCenter: number;
}

interface KickParams {
  kickSpeed: number;
  kickPop: number;
}

interface DozerParams {
  dozerLaunchForce: number;
}

/** Result of a bulldozer ram: how the rammed car should spin out while stunned. */
export interface DozerHit {
  /** Steer sign (+/-1) for the victim's spin-out (which side it was hit from). */
  spin: number;
}

/**
 * Lateral wall clamp shared by cars and balls. Given the signed lateral offset
 * from the centreline and the road limit, returns how to push back inside and
 * the velocity with its into-the-wall component removed (plus a little scrape
 * restitution). Returns null if the body is within the limit.
 */
export function resolveWall(
  lateral: number,
  limit: number,
  rX: number,
  rZ: number,
  vx: number,
  vz: number,
  restitution: number,
): WallResolution | null {
  if (Math.abs(lateral) <= limit) return null;
  const sign = Math.sign(lateral);
  const over = lateral - sign * limit;
  const nx = rX * sign; // outward normal
  const nz = rZ * sign;
  const into = vx * nx + vz * nz;
  let nvx = vx;
  let nvz = vz;
  if (into > 0) {
    const k = 1 + restitution;
    nvx -= k * into * nx;
    nvz -= k * into * nz;
  }
  return { pushX: -rX * over, pushZ: -rZ * over, vx: nvx, vz: nvz };
}

/**
 * Flat wall PLANE across the road (M42) — the barrier behind the start grid and
 * the one closing the finish run-out, so nothing rolls off either end of the
 * course. `wx/wz` is a point on the wall and `nx/nz` its unit normal pointing
 * INTO the course; the body's centre must stay at least `radius` inside.
 * Returns the push + reflected velocity, or null when clear (the common case).
 */
export function resolvePlaneWall(
  px: number,
  pz: number,
  radius: number,
  wx: number,
  wz: number,
  nx: number,
  nz: number,
  vx: number,
  vz: number,
  restitution: number,
): WallResolution | null {
  const d = (px - wx) * nx + (pz - wz) * nz; // signed distance inside the course
  if (d >= radius) return null;
  const over = radius - d;
  const into = -(vx * nx + vz * nz); // speed toward the wall
  let nvx = vx;
  let nvz = vz;
  if (into > 0) {
    const k = 1 + restitution;
    nvx += k * into * nx;
    nvz += k * into * nz;
  }
  return { pushX: nx * over, pushZ: nz * over, vx: nvx, vz: nvz };
}

/** Half-thickness of a planted FREE KICK WALL (M46) along the track direction. */
export const FREE_KICK_WALL_HALF_THICK = 0.5;

/**
 * A planted FREE KICK WALL (M46): a short cross-road barrier frozen in world
 * space where it was dropped. `x/z` is its centre, `dirX/dirZ` the track
 * forward there (unit), `rightX/rightZ` the track right (unit), `halfSpan` its
 * lateral half-extent. Pure data — SimCore owns the list, the client draws it.
 */
export interface FreeKickWallShape {
  x: number;
  z: number;
  dirX: number;
  dirZ: number;
  rightX: number;
  rightZ: number;
  halfSpan: number;
}

/**
 * Body (car circle or ball sphere) vs a planted free-kick wall. The wall is a
 * thin slab across the road: bodies outside its lateral span pass freely;
 * bodies hitting a face are pushed back out the side they came from with a
 * reflected velocity (`restitution` like the guardrails). Returns null when
 * clear (the common case). Pure + deterministic.
 */
export function resolveFreeKickWall(
  px: number,
  pz: number,
  radius: number,
  wall: FreeKickWallShape,
  vx: number,
  vz: number,
  restitution: number,
): WallResolution | null {
  const dx = px - wall.x;
  const dz = pz - wall.z;
  const lat = dx * wall.rightX + dz * wall.rightZ;
  if (Math.abs(lat) > wall.halfSpan) return null; // passing beside the wall
  const along = dx * wall.dirX + dz * wall.dirZ;
  const reach = radius + FREE_KICK_WALL_HALF_THICK;
  if (Math.abs(along) >= reach) return null;
  // Push back out the side the body is (still) on.
  const side = along >= 0 ? 1 : -1;
  const nx = wall.dirX * side;
  const nz = wall.dirZ * side;
  const over = reach - Math.abs(along);
  const into = -(vx * nx + vz * nz); // speed toward the wall face
  let nvx = vx;
  let nvz = vz;
  if (into > 0) {
    const k = 1 + restitution;
    nvx += k * into * nx;
    nvz += k * into * nz;
  }
  return { pushX: nx * over, pushZ: nz * over, vx: nvx, vz: nvz };
}

/**
 * BALL vs a planted free-kick wall — the DIVERTER version (M51, owner rework).
 * Instead of firing the ball back down-course (a wall hit could cost half a
 * minute), the wall deflects it SIDEWAYS: out toward the near end of the wall
 * span — and on into the guardrail — at roughly a right angle to its arrival,
 * keeping `keep` of its speed. The race keeps flowing; the wall just re-routes.
 * Cars still use resolveFreeKickWall above (a soft bump).
 *
 * `blockedEnd` (M143c): +1 / -1 when THAT end of the wall stands against the
 * guardrail with no room for a ball to pass (0 = both ends open). A wall is
 * planted on the firer's lane, clamped onto the road, so a firer near a rail
 * leaves a wall whose end touches it. "Out the near end" is then a dead end:
 * the ball is sent into the corner, the rail sends it back, and with its own
 * car pushing from behind it is squeezed there (measured: the squeeze that
 * launched balls skyward). Such a ball leaves by the OPEN end instead.
 */
export function deflectFreeKickWall(
  px: number,
  pz: number,
  radius: number,
  wall: FreeKickWallShape,
  vx: number,
  vz: number,
  keep: number,
  blockedEnd = 0,
): WallResolution | null {
  const dx = px - wall.x;
  const dz = pz - wall.z;
  const lat = dx * wall.rightX + dz * wall.rightZ;
  if (Math.abs(lat) > wall.halfSpan) return null;
  const along = dx * wall.dirX + dz * wall.dirZ;
  const reach = radius + FREE_KICK_WALL_HALF_THICK;
  if (Math.abs(along) >= reach) return null;
  const side = along >= 0 ? 1 : -1;
  const nx = wall.dirX * side;
  const nz = wall.dirZ * side;
  const over = reach - Math.abs(along);
  const into = -(vx * nx + vz * nz);
  let nvx = vx;
  let nvz = vz;
  if (into > 0) {
    // Deflect out the NEAR end of the wall (dead-centre picks + deterministically).
    let latSign = Math.sign(lat) || 1;
    if (blockedEnd !== 0 && latSign === blockedEnd) latSign = -blockedEnd;
    const speed = Math.hypot(vx, vz);
    // ~66° off the wall normal: mostly sideways, a touch of back-off to clear it.
    let ddx = wall.rightX * latSign * 0.92 + nx * 0.4;
    let ddz = wall.rightZ * latSign * 0.92 + nz * 0.4;
    const m = Math.hypot(ddx, ddz) || 1;
    ddx /= m;
    ddz /= m;
    nvx = ddx * speed * keep;
    nvz = ddz * speed * keep;
  }
  return { pushX: nx * over, pushZ: nz * over, vx: nvx, vz: nvz };
}

/**
 * Ball vs car (sphere vs oriented box). Resolves penetration, then carries the
 * ball along the contact normal at the car's pushing speed (plus a push gain),
 * pops it up a touch on hard hits, and clamps the ball speed. Only the ball is
 * modified — the car is far heavier and plows through. Returns true on contact.
 *
 * M12 dribble cradle: contacts on the FRONT face additionally (a) bend the push
 * direction toward the car's heading (`dribbleAlign`) so the ball is carried
 * where the car is GOING rather than where the contact corner points, and (b)
 * damp the ball's sideways slip relative to the car (`dribbleLateralGrip`, needs
 * `dt`). Together these keep a full-speed dribble centred on the nose instead of
 * squirting off a corner — without sticking the ball to the car.
 */
export function resolveBallCar(
  ball: BallBody,
  car: CarBox,
  p: BallPushParams,
  dt: number,
  box: CarBoxDims = STOCK_CAR_BOX,
): boolean {
  const fx = forwardX(car.heading);
  const fz = forwardZ(car.heading);
  const rx = rightX(car.heading);
  const rz = rightZ(car.heading);
  const halfL = box.halfLength;
  const halfW = box.halfWidth;

  // Ball centre in the car's local (forward, right) frame.
  const dx = ball.x - car.x;
  const dz = ball.z - car.z;
  const localF = dx * fx + dz * fz;
  const localR = dx * rx + dz * rz;
  const clF = clamp(localF, -halfL, halfL);
  const clR = clamp(localR, -halfW, halfW);

  let nF: number;
  let nR: number;
  let pen: number;
  const inside = clF === localF && clR === localR;
  if (!inside) {
    const dF = localF - clF;
    const dR = localR - clR;
    const d = Math.hypot(dF, dR);
    if (d >= ball.radius) return false;
    pen = ball.radius - d;
    if (d > 1e-6) {
      nF = dF / d;
      nR = dR / d;
    } else {
      nF = 0;
      nR = Math.sign(localR) || 1;
    }
  } else {
    // Ball centre inside the box footprint — push out the nearest face.
    const penF = halfL - Math.abs(localF);
    const penR = halfW - Math.abs(localR);
    if (penR <= penF) {
      nR = Math.sign(localR) || 1;
      nF = 0;
      pen = penR + ball.radius;
    } else {
      nF = Math.sign(localF) || 1;
      nR = 0;
      pen = penF + ball.radius;
    }
  }

  // Contact normal in world space.
  const nx = fx * nF + rx * nR;
  const nz = fz * nF + rz * nR;

  // Resolve penetration (always along the TRUE normal — geometry stays honest).
  ball.x += nx * pen;
  ball.z += nz * pen;

  // The cradle engages on nose contacts while the car genuinely drives forward.
  const carFwd = car.vx * fx + car.vz * fz;
  const cradling = nF > CRADLE_MIN_FRONTNESS && carFwd > CRADLE_MIN_SPEED;

  // Push direction: the true normal, bent toward the car's heading on the nose.
  let px = nx;
  let pz = nz;
  if (cradling && p.dribbleAlign > 0) {
    const k = p.dribbleAlign * nF;
    px = nx * (1 - k) + fx * k;
    pz = nz * (1 - k) + fz * k;
    const m = Math.hypot(px, pz) || 1;
    px /= m;
    pz /= m;
  }

  // Momentum along the push direction.
  const carVN = car.vx * px + car.vz * pz;
  const ballVN = ball.vx * px + ball.vz * pz;
  const approach = carVN - ballVN;
  if (approach > 0) {
    // Dribble: carry the ball up to the car's pushing speed so it stays just in
    // front and controllable (rather than rocketing away).
    ball.vx += px * approach;
    ball.vz += pz * approach;
    // Hard ram: an extra kick (+ a little upward pop) scaled by carPush.
    if (approach > HARD_HIT) {
      const kick = (approach - HARD_HIT) * p.carPush;
      ball.vx += px * kick;
      ball.vz += pz * kick;
      if (ball.vy < POP_CEILING) {
        ball.vy = Math.min(ball.vy + Math.min((approach - HARD_HIT) * 0.2, MAX_POP), POP_CEILING);
      }
    }
  }

  // Cradle: a small PD controller that settles the ball onto the MIDDLE of the
  // nose — damp its sideways slip relative to the car (D) and pull it gently
  // toward the centreline (P). Strong enough to hold a dribble through normal
  // racing-line corrections; a hard cut still out-accelerates it and sheds the
  // ball (that's the skill ceiling, tuned via dribbleLateralGrip/dribbleCenter).
  if (cradling && dt > 0) {
    const relR = (ball.vx - car.vx) * rx + (ball.vz - car.vz) * rz;
    const damp = relR * Math.min(p.dribbleLateralGrip * dt, 1) + p.dribbleCenter * localR * dt;
    ball.vx -= rx * damp;
    ball.vz -= rz * damp;
  }

  // Cap horizontal ball speed.
  const hs = Math.hypot(ball.vx, ball.vz);
  if (hs > p.maxSpeed) {
    const k = p.maxSpeed / hs;
    ball.vx *= k;
    ball.vz *= k;
  }
  return true;
}

/**
 * Magnet power-up: pin the ball to a point just ahead of the car's nose and give
 * it the car's velocity, so it tracks the car through corners and, when the
 * magnet releases, carries that momentum forward. Caller handles ground height
 * and wall clamping (this is pure planar geometry). Mutates `ball`.
 */
export function magnetHold(ball: BallBody, car: CarBox, gap: number, box: CarBoxDims = STOCK_CAR_BOX): void {
  const fx = forwardX(car.heading);
  const fz = forwardZ(car.heading);
  const front = box.halfLength + ball.radius + gap;
  ball.x = car.x + fx * front;
  ball.z = car.z + fz * front;
  ball.vx = car.vx;
  ball.vz = car.vz;
}

export interface KidAssistParams {
  range: number;
  holdGap: number;
  gain: number;
  pull: number;
}

/**
 * BIG KID ball leash (M83) — the SOFT cousin of magnetHold: instead of pinning
 * the ball, steer its VELOCITY toward "ride with the car + close the offset to
 * the nose spot", with the per-second change capped at `pull` so the ball
 * visibly rolls home rather than teleporting. No effect beyond `range` (a real
 * punt escapes the leash until the car comes back for it). Planar only — the
 * caller's normal ground/bounce physics keep owning y. Mutates `ball`; returns
 * whether the leash engaged.
 */
export function kidAssistPull(
  ball: BallBody,
  car: CarBox,
  p: KidAssistParams,
  dt: number,
  box: CarBoxDims = STOCK_CAR_BOX,
): boolean {
  const fx = forwardX(car.heading);
  const fz = forwardZ(car.heading);
  const front = box.halfLength + ball.radius + p.holdGap;
  const dx = car.x + fx * front - ball.x;
  const dz = car.z + fz * front - ball.z;
  if (Math.hypot(dx, dz) > p.range) return false;
  const dvx = car.vx + dx * p.gain - ball.vx;
  const dvz = car.vz + dz * p.gain - ball.vz;
  const dv = Math.hypot(dvx, dvz);
  if (dv < 1e-9) return true;
  const k = Math.min(1, (p.pull * dt) / dv);
  ball.vx += dvx * k;
  ball.vz += dvz * k;
  return true;
}

export interface PossessionParams {
  range: number;
  holdGap: number;
  gain: number;
  pull: number;
  breakSpeed: number;
  recedeSpeed: number;
  minCarSpeed: number;
}

/**
 * POSSESSION TOUCH (M84) — the everyday, much weaker cousin of kidAssistPull:
 * while a racer genuinely DRIBBLES its own ball, gently steer the ball's
 * velocity to ride just off the nose (capped at `pull` per second — close
 * control, never a magnet). Engages only when ALL of these hold:
 *  - the car is driving forward (> minCarSpeed along its heading);
 *  - the ball is IN FRONT of the car (a ball behind you is not "in
 *    possession" — drive back to it) and within `range` of the nose spot;
 *  - the ball's velocity rides close to the car's (divergence < breakSpeed).
 *    This one gate IS the possession-loss rule: any real knock (rival car or
 *    ball, item, wall rebound) throws the ball outside the envelope, and the
 *    divergence in a turn grows with speed × turn angle, so a slow tight turn
 *    keeps the dribble while a hard cut or hard brake at top speed sheds it;
 *  - the ball is not OUTRUNNING the car along its forward axis faster than
 *    `recedeSpeed` — that's a deliberate strike (a punt must fly, not get
 *    dragged back), and the same gate lets the ball run off under hard
 *    braking (the car slows, the ball doesn't — possession lost).
 * Planar only — the caller's ground/bounce physics keep owning y. Mutates
 * `ball`; returns whether the touch engaged.
 */
export function possessionPull(
  ball: BallBody,
  car: CarBox,
  p: PossessionParams,
  dt: number,
  box: CarBoxDims = STOCK_CAR_BOX,
): boolean {
  const fx = forwardX(car.heading);
  const fz = forwardZ(car.heading);
  if (car.vx * fx + car.vz * fz < p.minCarSpeed) return false;
  // Dribbling only: the ball centre must be ahead of the car centre.
  if ((ball.x - car.x) * fx + (ball.z - car.z) * fz < 0) return false;
  const front = box.halfLength + ball.radius + p.holdGap;
  const dx = car.x + fx * front - ball.x;
  const dz = car.z + fz * front - ball.z;
  const dist = Math.hypot(dx, dz);
  if (dist > p.range) return false;
  const rvx = ball.vx - car.vx;
  const rvz = ball.vz - car.vz;
  if (Math.hypot(rvx, rvz) > p.breakSpeed) return false; // knocked/cut loose
  if (rvx * fx + rvz * fz > p.recedeSpeed) return false; // a strike (or the ball outrunning a braking car)
  const dvx = car.vx + dx * p.gain - ball.vx;
  const dvz = car.vz + dz * p.gain - ball.vz;
  const dv = Math.hypot(dvx, dvz);
  if (dv < 1e-9) return true;
  const k = Math.min(1, (p.pull * dt) / dv);
  ball.vx += dvx * k;
  ball.vz += dvz * k;
  return true;
}

/**
 * Car vs car: circle separation + momentum exchange (full-chaos bumping).
 * `aFactor`/`bFactor` scale how much each car is shoved (1 = normal). A SHIELDED
 * car passes its `shieldBumpFactor` here and barely budges — it bumps like a
 * truck while the other car takes the bounce.
 */
export function resolveCarCar(
  a: CarBox,
  b: CarBox,
  restitution = 0.3,
  aFactor = 1,
  bFactor = 1,
): boolean {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const d = Math.hypot(dx, dz);
  const minD = CAR_RADIUS * 2;
  if (d >= minD || d < 1e-6) return false;

  const nx = dx / d;
  const nz = dz / d;
  const pen = minD - d;

  // Separate in proportion to each car's shove factor (equal halves at 1/1).
  const sum = aFactor + bFactor || 1;
  a.x -= nx * pen * (aFactor / sum);
  a.z -= nz * pen * (aFactor / sum);
  b.x += nx * pen * (bFactor / sum);
  b.z += nz * pen * (bFactor / sum);

  // Exchange normal momentum if they're approaching. The factors act as inverse
  // masses: at 1/1 this is the classic equal split; against a shielded (heavy)
  // car the impulse grows and the unshielded car takes almost all of the bounce.
  const avn = a.vx * nx + a.vz * nz;
  const bvn = b.vx * nx + b.vz * nz;
  const rel = avn - bvn;
  if (rel > 0) {
    const impulse = ((1 + restitution) * rel) / sum;
    a.vx -= impulse * nx * aFactor;
    a.vz -= impulse * nz * aFactor;
    b.vx += impulse * nx * bFactor;
    b.vz += impulse * nz * bFactor;
  }
  return true;
}

/** M57 weight-clash knobs (tuning.clash). */
export interface ClashParams {
  /** Weight ratio at/above which the lighter car fully SPINS OUT. */
  spinRatio: number;
  /** Ratio at/above which it STAGGERS (a beat of lost steering). */
  staggerRatio: number;
  /** Closing speed (u/s along the contact normal) that counts as a real hit. */
  minImpactSpeed: number;
}
export interface ClashHit {
  victim: 'a' | 'b';
  tier: 'spin' | 'stagger';
  spin: 1 | -1;
}

/**
 * M57 weight-clash: when two cars of meaningfully different WEIGHT collide at
 * real speed, the LIGHTER one loses composure — a full spin-out at truck-vs-
 * bike disparity, a brief stagger at the middle tier, nothing extra below
 * (the plain resolveCarCar shove already scales by 1/weight). PURE decision
 * function that mutates nothing: call it BEFORE resolveCarCar (it reads the
 * pre-impulse velocities); the caller applies the stun. Null = no clash.
 */
export function resolveCarClash(
  a: CarBox,
  b: CarBox,
  weightA: number,
  weightB: number,
  p: ClashParams,
): ClashHit | null {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const d = Math.hypot(dx, dz);
  if (d >= CAR_RADIUS * 2 || d < 1e-6) return null;

  const ratio = Math.max(weightA, weightB) / Math.max(1e-6, Math.min(weightA, weightB));
  if (ratio < p.staggerRatio) return null;

  // Closing speed along the contact normal, pre-impulse (same `rel` as the
  // bump in resolveCarCar) — a slow rub never spins anyone.
  const nx = dx / d;
  const nz = dz / d;
  const closing = (a.vx - b.vx) * nx + (a.vz - b.vz) * nz;
  if (closing < p.minImpactSpeed) return null;

  const victim: 'a' | 'b' = weightA >= weightB ? 'b' : 'a';
  const heavy = victim === 'a' ? b : a;
  const vic = victim === 'a' ? a : b;
  // Spin sign = which side of the heavy car's nose the victim sat on (the
  // bulldozer-hit convention).
  const sx = vic.x - heavy.x;
  const sz = vic.z - heavy.z;
  const side = sx * rightX(heavy.heading) + sz * rightZ(heavy.heading);
  return {
    victim,
    tier: ratio >= p.spinRatio ? 'spin' : 'stagger',
    spin: (Math.sign(side) || 1) as 1 | -1,
  };
}

/**
 * KICK power-up (M12): a soccer long-pass — fire the ball hard along the car's
 * heading with a small hop. Velocity is OVERWRITTEN for a decisive, aimable
 * strike (you point the car, the ball goes there). The race layer gates this on
 * the ball being within `ballRange` of the kicker. Mutates `ball`.
 */
export function kickBall(ball: BallBody, car: CarBox, p: KickParams): void {
  const fx = forwardX(car.heading);
  const fz = forwardZ(car.heading);
  ball.vx = fx * p.kickSpeed;
  ball.vz = fz * p.kickSpeed;
  ball.vy = Math.max(ball.vy, p.kickPop);
}

/**
 * Bulldozer ram: when a dozer-active car touches a rival, the rammer plows
 * through (unchanged) and the victim is shoved clear and FLUNG outward along the
 * contact normal at the launch force (velocity overwritten for a clean fling).
 * Returns the spin sign for the caller to spin + stun the victim, or null if the
 * two cars aren't actually in contact. Only the victim is modified here.
 */
export function resolveDozerHit(rammer: CarBox, victim: CarBox, p: DozerParams): DozerHit | null {
  const dx = victim.x - rammer.x;
  const dz = victim.z - rammer.z;
  const d = Math.hypot(dx, dz);
  const minD = CAR_RADIUS * 2;
  if (d >= minD || d < 1e-6) return null;

  const nx = dx / d;
  const nz = dz / d;
  const pen = minD - d;

  // Shove the victim fully clear; the heavy rammer plows on untouched.
  victim.x += nx * pen;
  victim.z += nz * pen;

  // Fling it outward at the launch force (overwrite for a decisive launch).
  victim.vx = nx * p.dozerLaunchForce;
  victim.vz = nz * p.dozerLaunchForce;

  // Spin sign = which side of the rammer's nose the victim sat on.
  const side = nx * rightX(rammer.heading) + nz * rightZ(rammer.heading);
  return { spin: Math.sign(side) || 1 };
}

/** Ball vs ball: elastic-ish equal-mass sphere collision (3D). */
export function resolveBallBall(a: BallBody, b: BallBody, restitution = 0.8): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const d = Math.hypot(dx, dy, dz);
  const minD = a.radius + b.radius;
  if (d >= minD || d < 1e-6) return false;

  const nx = dx / d;
  const ny = dy / d;
  const nz = dz / d;
  const pen = minD - d;

  a.x -= nx * (pen / 2);
  a.y -= ny * (pen / 2);
  a.z -= nz * (pen / 2);
  b.x += nx * (pen / 2);
  b.y += ny * (pen / 2);
  b.z += nz * (pen / 2);

  const avn = a.vx * nx + a.vy * ny + a.vz * nz;
  const bvn = b.vx * nx + b.vy * ny + b.vz * nz;
  const rel = avn - bvn;
  if (rel > 0) {
    const impulse = ((1 + restitution) * rel) / 2;
    a.vx -= impulse * nx;
    a.vy -= impulse * ny;
    a.vz -= impulse * nz;
    b.vx += impulse * nx;
    b.vy += impulse * ny;
    b.vz += impulse * nz;
  }
  return true;
}
